import React from 'react';
import { SearchOutlined, ReloadOutlined, HistoryOutlined } from '@ant-design/icons';
import { VeritasSpinner } from '../../../../components/VeritasSpinner';
import { useOneClickPrescribeContext } from '../../context/OneClickPrescribeContext';
import { RequestTypeEnum } from '../../../../types/enums';
import LogCard from './LogCard';
import {
    LogsContainer,
    FilterPanel,
    FilterGroup,
    FilterLabel,
    FilterInput,
    FilterSelect,
    FilterActions,
    SearchButton,
    ResetButton,
    ResultsSection,
    ResultsHeader,
    ResultsCount,
    LogsList,
    EmptyState,
    EmptyStateIcon,
    EmptyStateText,
    EmptyStateSubText,
    PaginationRow,
    PageButton,
} from './styles';

const Logs = () => {
    const { cevLogs } = useOneClickPrescribeContext();

    const {
        filters,
        categories,
        providers,
        logs,
        total,
        page,
        limit,
        loading,
        categoriesLoading,
        providersLoading,
        hasSearched,
        handleFilterChange,
        handleSearch,
        handlePageChange,
        handleReset,
    } = cevLogs;

    const totalPages = Math.ceil(total / limit);

    const renderPagination = () => {
        if (totalPages <= 1) return null;
        const pages = [];
        for (let i = 1; i <= totalPages; i++) {
            pages.push(
                <PageButton
                    key={i}
                    $active={i === page}
                    onClick={() => handlePageChange(i)}
                    disabled={loading}
                >
                    {i}
                </PageButton>
            );
        }
        return (
            <PaginationRow>
                <PageButton
                    onClick={() => handlePageChange(page - 1)}
                    disabled={page <= 1 || loading}
                >
                    ‹
                </PageButton>
                {pages}
                <PageButton
                    onClick={() => handlePageChange(page + 1)}
                    disabled={page >= totalPages || loading}
                >
                    ›
                </PageButton>
            </PaginationRow>
        );
    };

    return (
        <LogsContainer>
            <FilterPanel>
                <FilterGroup>
                    <FilterLabel>Completed From</FilterLabel>
                    <FilterInput
                        type="date"
                        value={filters.completedAtStart}
                        onChange={e => handleFilterChange('completedAtStart', e.target.value)}
                        max={filters.completedAtEnd || undefined}
                    />
                </FilterGroup>

                <FilterGroup>
                    <FilterLabel>Completed To</FilterLabel>
                    <FilterInput
                        type="date"
                        value={filters.completedAtEnd}
                        onChange={e => handleFilterChange('completedAtEnd', e.target.value)}
                        min={filters.completedAtStart || undefined}
                    />
                </FilterGroup>

                <FilterGroup>
                    <FilterLabel>Program</FilterLabel>
                    <FilterSelect
                        value={filters.categoryId}
                        onChange={e => handleFilterChange('categoryId', e.target.value)}
                        disabled={categoriesLoading}
                    >
                        <option value="">All Programs</option>
                        {categories.map(cat => (
                            <option key={cat._id} value={cat._id}>
                                {cat.name}
                            </option>
                        ))}
                    </FilterSelect>
                </FilterGroup>

                <FilterGroup>
                    <FilterLabel>Provider</FilterLabel>
                    <FilterSelect
                        value={filters.providerId}
                        onChange={e => handleFilterChange('providerId', e.target.value)}
                        disabled={providersLoading}
                    >
                        <option value="">All Providers</option>
                        {providers.map(p => (
                            <option key={p._id} value={p._id}>
                                {p.firstName} {p.lastName}
                            </option>
                        ))}
                    </FilterSelect>
                </FilterGroup>

                <FilterGroup>
                    <FilterLabel>Request Type</FilterLabel>
                    <FilterSelect
                        value={filters.requestType}
                        onChange={e => handleFilterChange('requestType', e.target.value)}
                    >
                        <option value="">All</option>
                        <option value={RequestTypeEnum.INITIAL}>Initial</option>
                        <option value={RequestTypeEnum.RX_RENEWAL}>Rx Renewal</option>
                    </FilterSelect>
                </FilterGroup>

                <FilterActions>
                    <SearchButton onClick={handleSearch} disabled={loading}>
                        <SearchOutlined />
                        Search
                    </SearchButton>
                    {hasSearched && (
                        <ResetButton onClick={handleReset} disabled={loading}>
                            <ReloadOutlined />
                            Reset
                        </ResetButton>
                    )}
                </FilterActions>
            </FilterPanel>

            {loading && (
                <EmptyState>
                    <VeritasSpinner size="default" tip="Loading logs..." />
                </EmptyState>
            )}

            {!loading && hasSearched && (
                <ResultsSection>
                    <ResultsHeader>
                        <ResultsCount>
                            {total === 0
                                ? 'No results found'
                                : `Showing ${(page - 1) * limit + 1}–${Math.min(page * limit, total)} of ${total} result${total !== 1 ? 's' : ''}`}
                        </ResultsCount>
                    </ResultsHeader>

                    {logs.length === 0 ? (
                        <EmptyState>
                            <EmptyStateIcon>
                                <HistoryOutlined />
                            </EmptyStateIcon>
                            <EmptyStateText>No completed CEV cases found</EmptyStateText>
                            <EmptyStateSubText>Try adjusting the date range or program filter.</EmptyStateSubText>
                        </EmptyState>
                    ) : (
                        <>
                            <LogsList>
                                {logs.map(log => (
                                    <LogCard key={log.requestId} log={log} />
                                ))}
                            </LogsList>
                            {renderPagination()}
                        </>
                    )}
                </ResultsSection>
            )}

        </LogsContainer>
    );
};

export default Logs;
